import './bottommenu.css'

import React, { useRef, useState } from "react";
import { Popup } from "@progress/kendo-react-popup";
import { useEffect } from "react";
import {
    isArrayEmpty,
    isArraysEqual,
    isNullOrEmpty
} from '../../../helpers/ObjectHelpers';
import {
    getElementById,
    getElementsByClassName,
    scrollElementIntoView
} from '../../../helpers/ElementHelpers';
import Slide from '../../Slide';
import { BottomMenuIcon } from './components/BottomMenuIcon';
import { Asterisk, Eye } from 'phosphor-react-sc';
import parse from 'html-react-parser';
import { usePilotageStoreContext } from "components/hooks/usePilotageStoreContext";
import { useStoreSelector } from "components/hooks/useStoreSelector";

const hasSelectedItems = (obj:any, menuParam:string): boolean => {
    if (isArrayEmpty(obj.items)) return false;

    let filteredItems = obj.items.filter((o: any) => o.event.includes(menuParam));
    if (!isArrayEmpty(filteredItems)) return true;

    let result = false;
    for (const item of obj.items) {
        result = hasSelectedItems(item, menuParam);
        if (result) break;
    }

    return result;
}

interface BottomMenuProps {
    items: any,
    selectedEvent: string,
    onCallback?: (event: any) => void,
    fixed?: boolean,
}
export const BottomMenu = ({
    items = [],
    selectedEvent,
    onCallback,
    fixed = false,
}: BottomMenuProps) => {
    const parentMinimumWidth = 90;

    const [localItems, setLocalItems] = useState<any>([]);
    const [localSelectedEvent, setLocalSelectedEvent] = useState<string>(selectedEvent);

    useEffect(() => {

        const initialize = () => {
            const maxParents = Math.floor(window.innerWidth / parentMinimumWidth);

            if (fixed) {
                setLocalItems(items);
            } else {
                const result = [];
                const moreResult = [];

                for (let i = 0; i < items.length; i++) {
                    const item = items[i];

                    if (i < maxParents - 1) {
                        result.push(item);
                    } else {
                        moreResult.push(item);
                    }
                }

                if (!isArrayEmpty(moreResult)) {
                    if (moreResult.length > 1) {
                        const moreItem = {
                            title: "Mer",
                            icon: "dots",
                            event: "MORE",
                            items: moreResult
                        }
                        result.push(moreItem);
                    } else {
                        result.push(moreResult[0]);
                    }

                }

                if (!isArraysEqual(result, localItems)) {
                    setLocalItems(result);
                }
            }
        }

        initialize();

        window.addEventListener("resize", initialize);
        return () => {
            window.removeEventListener("resize", initialize);
        };
    }, [items]);

    return (
        <nav id="bottom-menu"
            className="k-bottom-nav k-bottom-nav-item-flow-vertical k-bottom-nav-flat k-bottom-nav-flat-primary k-bottom-nav-border bottom-navigation"
            dir="ltr">
            {
                localItems.map((item:any, index:number) =>
                    <BottomMenuItem
                        key={index}
                        item={item}
                        selectedEvent={selectedEvent}
                        localSelectedEvent={localSelectedEvent}
                        setLocalSelectedEvent={setLocalSelectedEvent}
                        onClick={onCallback}
                        onItemClick={onItemClick}
                    />
                )
            }
        </nav>
    )

    function onItemClick(e: any) {
        if (!onCallback) return;
        onCallback(e);
    }
}

interface BottomMenuItemProps {
    item: any,
    localSelectedEvent: string,
    setLocalSelectedEvent: (event: any) => void,
    selectedEvent: string,
    onClick?: (event: any) => void,
    onItemClick?: (event: any) => void,
}
const BottomMenuItem = ({
    item,
    localSelectedEvent,
    setLocalSelectedEvent,
    selectedEvent,
    onClick,
    onItemClick,
}: BottomMenuItemProps) => {

    const anchor = useRef<HTMLDivElement | null>(null);

    const [show, setShow] = useState(false);
    const [renderPopup, setRenderPopup] = useState(false);
    const [popupWidth, setPopupWidth] = useState(0);
    const [popupLeft, setPopupLeft] = useState(0);
    const [isSelected, setIsSelected] = useState(false);
    const [hasChanges, setHasChanges] = useState(item.hasChanges);
    const [hasObservations, setHasObservations] = useState(item.hasObservations);

    useEffect(() => {

        const initialize = () => {
            if (!anchor.current) return;
            setPopupWidth(anchor.current.clientWidth);
            setPopupLeft(anchor.current.offsetLeft);
        }

        const onResize = () => {
            setDimensions();
        }

        toggleOverlay();
        initialize();

        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, []);

    useEffect(() => {
        if (item.event === localSelectedEvent) return;
        resetPopupClassList();
        setShow(false);
    }, [localSelectedEvent]);

    useEffect(() => {
        if (isNullOrEmpty(selectedEvent) && isArrayEmpty(item.items)) {
            setIsSelected(true);
        } else {
            const isSelected = selectedEvent?.includes(item.event) || hasSelectedItems(item, selectedEvent);
            setIsSelected(isSelected);
        }
    }, [selectedEvent]);

    useEffect(() => {
        if (show) {
            setRenderPopup(!isArrayEmpty(item.items));
        } else {
            setTimeout(() => {
                setRenderPopup(false);
            }, 500);
        }
    }, [show]);


    const pilotageStore = usePilotageStoreContext();
    const pilotageHasObservation = pilotageStore
        ? useStoreSelector(pilotageStore, (state) => state.hasObservation)
        : undefined;
    const pilotageHasChanges = pilotageStore
        ? useStoreSelector(pilotageStore, (state) => state.hasChanges)
        : undefined;

    useEffect(() => {
        if (pilotageHasObservation === undefined || !(item.hasOwnProperty('hasObservations'))) return;
        setHasObservations(pilotageHasObservation);
    }, [pilotageHasObservation]);

    useEffect(() => {
        if (pilotageHasChanges === undefined || !(item.hasOwnProperty('hasChanges'))) return;
        setHasChanges(pilotageHasChanges);
    }, [pilotageHasChanges]);

    return (
        <>
            <div
                id={`BottomMenu-${item.event}`}
                ref={anchor}
                className={`k-bottom-nav-item ${isSelected ? "k-selected" : ""}`}
                onClick={handleClick}>
                <div className="text-center k-pt-1 btm-nav-icon">
                    <span className="k-icon k-svg-icon k-svg-i-ToolboxIcon k-icon-xl" aria-hidden="true">
                        <BottomMenuIcon icon={item.icon} />
                    </span>
                </div>
                <div className="text-truncate prevent-selection text-center k-align-self-start btm-nav-text">
                    {parse(item.title)}
                    {
                        !isNullOrEmpty(item.subTitle) && <sup>&nbsp;{parse(item.subTitle)}</sup>
                    }
                </div>
                {
                    (hasChanges) && <div className="btm-nav-changes"><Asterisk size={14} weight="bold" /></div>
                }
                {
                    (hasObservations) && <div className="btm-nav-changes"><Eye size={14} weight="bold" /></div>
                }
            </div>

            {
                (renderPopup) &&
                <Popup
                    id={`BottomMenuPopup-${item.event}`}
                    anchor={anchor.current}
                    anchorAlign={{ horizontal: "left", vertical: "top" }}
                    popupAlign={{ horizontal: "left", vertical: "bottom" }}
                    show={show}
                    onOpen={onPopupOpen}
                    className="bn-p overflow-hidden"
                    style={{
                        width: popupWidth,
                        left: popupLeft,
                        
                    }}>
                    <div className="bn-pc">
                        <ul>
                                {
                                    item.items.map((childItem: any, index: number) =>
                                    <BottomMenuPopupItem
                                        key={index}
                                        item={childItem}
                                        selectedEvent={selectedEvent}
                                        handleOnItemClick={handleOnItemClick}
                                    />)
                            }
                        </ul>
                    </div>
                </Popup>
            }

        </>
    )

    function handleClick(e: any) {
        e.stopPropagation();

        if (!show === false) {
            resetPopupClassList();
        } else {
            updatePopupClassList();
        }

        setShow(!show);
        setLocalSelectedEvent(item.event);

        if (!isArrayEmpty(item.items)) {
            toggleOverlay(!show);
        } else {
            if (onClick) {
                onClick(item);
            }
            toggleOverlay();
        }

    }

    function handleOnItemClick(e: any, item: any) {
        e.stopPropagation();

        resetPopupClassList();

        setShow(false);
        toggleOverlay();

        if(!onItemClick) return;
        setTimeout(() => {
            onItemClick(item);
        }, 250);
    }

    function onPopupOpen() {
        const elem = getElementById("BottomMenuPopup-" + item.event);
        if (!elem) return;
        elem.style.top = "unset";
        elem.style.bottom = "57px";

        elem.classList.remove("overflow-hidden");
        elem.classList.add("overflow-auto");

        setDimensions();
    }

    function updatePopupClassList() {
        setTimeout(() => {
            const elem = getElementById("BottomMenuPopup-" + item.event);
            if (!elem) return;
            elem.classList.add("bn-p-active");
        }, 750);
    }

    function resetPopupClassList() {
        const elem = getElementById("BottomMenuPopup-" + item.event);
        if (!elem) return;
        elem.classList.remove("overflow-auto");
        elem.classList.add("overflow-hidden");
    }

    function toggleOverlay(show = false) {
        const overlayClassName = "bn-overlay";
        const overlayId = `BottomMenuPopupOverlay`;
        const elem = getElementById(overlayId);

        if (!show) {
            if (!elem) return;
            elem.remove();

        } else {
            if (elem) return;
            const overlay = document.createElement("div");
            overlay.id = overlayId;
            overlay.classList.add(overlayClassName);
            overlay.onclick = () => {
                const elems = getElementsByClassName("bn-p");
                for (const elem of elems) {
                    elem.classList.remove("overflow-auto");
                    elem.classList.add("overflow-hidden");
                }

                setShow(false);
                toggleOverlay();
            };
            document.body.appendChild(overlay);
        }
    }

    function setDimensions() {
        const elem = getElementById("BottomMenu-" + item.event);
        if (elem) {
            setPopupWidth(elem.clientWidth);
            setPopupLeft(elem.offsetLeft);
        } else {
            setShow(false);
            toggleOverlay();
        }
    }
}

interface BottomMenuPopupItemProps {
    item: any,
    level?: number,
    selectedEvent: string,
    handleOnItemClick: (event: any, item: any) => void
}

const BottomMenuPopupItem = ({
    item,
    level = 0,
    selectedEvent,
    handleOnItemClick }: BottomMenuPopupItemProps) => {

    const [show, setShow] = useState(false);

    useEffect(() => {
        if (!hasSelectedItems(item, selectedEvent)) return;
        setTimeout(() => {
            setShow(true);
        }, 250);
    }, []);

    useEffect(() => {
        if (item.event !== selectedEvent) return;
        setTimeout(() => {
            scrollElementIntoView(`BottomMenuPopupItem-${item.event}`);
        }, 750);
    }, [show]);

    return (
        <li
            onClick={(e) => onPopupItemClick(e, item)}
            id={`BottomMenuPopupItem-${item.event}`}>
            <div
                style={{
                    paddingLeft: `${level * 10}px`
                }}

                className={`${selectedEvent === item.event ? "bn-pcs" : ""}`}>
                <div>
                    {
                        (!isNullOrEmpty(item.icon)) &&
                        <div className="k-ml-1">
                            <BottomMenuIcon icon={item.icon} />
                        </div>
                    }
                </div>
                <div className="k-ml-1 k-mr-1 text-truncate">{parse(item.title)}</div>
                <div>
                    {
                        (!isArrayEmpty(item.items)) &&
                        <div className="k-mr-1">
                            <BottomMenuIcon icon={show ? "chevronUp" : "chevronDown"} />
                        </div>
                    }
                </div>
            </div>
            {
                (!isArrayEmpty(item.items)) &&
                <Slide show={show}>
                    <ul>
                        {
                            item.items.map((item: any, index: number) =>
                                <BottomMenuPopupItem
                                    key={index}
                                    item={item}
                                    level={level + 1}
                                    selectedEvent={selectedEvent}
                                    handleOnItemClick={handleOnItemClick}
                                />
                            )
                        }
                    </ul>
                </Slide>
            }
        </li>
    )

    function onPopupItemClick(e: any, item: any) {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        if (!isArrayEmpty(item.items)) {
            setShow(!show);
        } else {
            handleOnItemClick(e, item);
        }
    }
}
